"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import "./pdf.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { computePricing, PRESETS } from "./pricing";

const DRAFT_KEY = "figmawya_pricing_draft_v10";

function nowISO() {
  return new Date().toISOString();
}

function formatDateAR(iso) {
  try {
    return new Date(iso).toLocaleString("ar-SA");
  } catch {
    return iso;
  }
}

function safeFileName(name) {
  return String(name || "project")
    .trim()
    .replaceAll(" ", "-")
    .replaceAll("/", "-")
    .replaceAll("\\", "-");
}

async function downloadPDF({ el, projectName }) {
  if (!el) return;

  // Important: html2canvas cannot capture elements with display:none
  // Ensure the PDF stage is rendered (offscreen) using CSS class .pdf-stage (not d-none)
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
  });

  if (!canvas.width || !canvas.height) {
    throw new Error("PDF capture failed: empty canvas. Ensure the report container is not display:none.");
  }

  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: "a4",
  });

  // A4 in mm (portrait)
  const pageW = 210;
  const pageH = 297;

  const margin = 10; // mm
  const contentW = pageW - margin * 2;
  const contentH = pageH - margin * 2;

  // Convert canvas px ratio to fit A4 width
  const imgW = contentW;
  const imgH = (canvas.height * imgW) / canvas.width;

  // Guard against NaN
  if (!Number.isFinite(imgW) || !Number.isFinite(imgH)) {
    throw new Error("Invalid PDF dimensions. Please retry.");
  }

  let heightLeft = imgH;
  let positionY = margin;

  // First page
  pdf.addImage(imgData, "PNG", margin, positionY, imgW, imgH);

  heightLeft -= contentH;

  // More pages
  while (heightLeft > 0) {
    pdf.addPage("a4", "p");
    positionY = margin - (imgH - heightLeft);
    pdf.addImage(imgData, "PNG", margin, positionY, imgW, imgH);
    heightLeft -= contentH;
  }

  pdf.save(`${safeFileName(projectName)}-figmawya.pdf`);
}

export default function CalculatorPage() {
  const [state, setState] = useState(defaultState());
  const [stepIndex, setStepIndex] = useState(0);

  const [draftGate, setDraftGate] = useState({ show: false, meta: null, draft: null });
  const [hydrated, setHydrated] = useState(false);

  // Avoid hydration mismatch: render date only after mount
  const [renderedAt, setRenderedAt] = useState("");

  const reportRef = useRef(null);

  useEffect(() => {
    setRenderedAt(new Date().toLocaleString("ar-SA"));

    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        const meta = draft?.meta || null;
        if (draft && typeof draft === "object") {
          setDraftGate({ show: true, meta, draft });
        }
      }
    } catch { }

    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      const payload = {
        version: 5,
        stepIndex,
        state,
        meta: {
          projectName: state.projectName || "",
          updatedAt: nowISO(),
        },
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch { }
  }, [state, stepIndex, hydrated]);

  const steps = useMemo(() => buildSteps(state), [state]);

  useEffect(() => {
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1);
  }, [steps.length, stepIndex]);

  const pricing = useMemo(() => {
    if (!isReadyForPricing(state)) return null;
    return computePricing(state);
  }, [state]);

  if (draftGate.show && draftGate.draft) {
    const name = draftGate.meta?.projectName || "مشروع بدون اسم";
    const updatedAt = draftGate.meta?.updatedAt ? formatDateAR(draftGate.meta.updatedAt) : "";

    return (
      <Shell>
        <h1 className="h4 fw-bold mb-3">حاسبة تسعير مشاريع التصميم</h1>

        <div className="card">
          <div className="card-body">
            <h2 className="h5 fw-bold mb-2">لديك عملية حساب لمشروع حالية</h2>
            <div className="text-muted mb-2">اسم المشروع: <b>{name}</b></div>
            {updatedAt ? <div className="text-muted mb-3">اخر تحديث: {updatedAt}</div> : null}

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const d = draftGate.draft;
                  setState((s) => ({ ...s, ...(d.state || {}) }));
                  setStepIndex(Number(d.stepIndex || 0));
                  setDraftGate({ show: false, meta: null, draft: null });
                }}
              >
                متابعة
              </button>

              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => {
                  localStorage.removeItem(DRAFT_KEY);
                  setState(defaultState());
                  setStepIndex(0);
                  setDraftGate({ show: false, meta: null, draft: null });
                }}
              >
                مشروع جديد
              </button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  if (state.userType && state.userType !== "freelancer") {
    return (
      <Shell>
        <div>
          <h1 className="h4 fw-bold mb-3">حاسبة تسعير مشاريع التصميم</h1>
          <div className="card">
            <div className="card-body">
              <h2 className="h5 fw-bold mb-2">اعتذار</h2>
              <p className="mb-2">النسخة الحالية مخصصة للفريلانسر "مستقل"</p>
              <p className="mb-3">قريبا بنوفر نسخة للموظفين والشركات</p></div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                localStorage.removeItem(DRAFT_KEY);
                setState(defaultState());
                setStepIndex(0);
                setDraftGate({ show: false, meta: null, draft: null });
              }}
            >
              رجوع
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  const current = steps[stepIndex];

  if (!current) return null;

  return (
    <Shell>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <h1 className="h4 fw-bold mb-0">حاسبة تسعير مشاريع التصميم</h1>

        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => {
            localStorage.removeItem(DRAFT_KEY);
            setState(defaultState());
            setStepIndex(0);
          }}
        >
          مسح البيانات
        </button>
      </div>

      <div className="row g-3">
        {current.id === "final" ? (
          <div className="col-12">
            <div className="card">
              <div className="card-body p-3 p-md-5">
                <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
                  <div>
                    <h2 className="h4 fw-bold mb-1">النتيجة النهائية</h2>
                    <div className="text-muted">ملخص التسعير النهائي للمشروع</div>
                  </div>

                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                    >
                      رجوع
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() =>
                        downloadPDF({
                          el: reportRef.current,
                          projectName: state.projectName,
                        })
                      }
                      disabled={!pricing}
                    >
                      تحميل PDF
                    </button>
                  </div>
                </div>

                {!pricing ? (
                  <div className="alert alert-secondary mb-0">اكمل البيانات الاساسية عشان يظهر التسعير</div>
                ) : (
                  <>
                    <div className="row g-3 mb-4">
                      <div className="col-12 col-lg-4">
                        <div className="border rounded p-4 h-100">
                          <div className="text-muted small">الادنى</div>
                          <div className="display-6 fw-bold mb-0">{formatUSD(pricing.minimum)}</div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-4">
                        <div className="border border-primary rounded p-4 h-100">
                          <div className="text-muted small">المقترح</div>
                          <div className="display-5 fw-bold mb-0">{formatUSD(pricing.recommended)}</div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-4">
                        <div className="border rounded p-4 h-100">
                          <div className="text-muted small">الاعلى</div>
                          <div className="display-6 fw-bold mb-0">{formatUSD(pricing.premium)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-lg-6">
                        <div className="card">
                          <div className="card-body">
                            <h3 className="h5 fw-bold mb-3">تفاصيل الحساب</h3>
                            <Breakdown pricing={pricing} state={state} />
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6">
                        <div className="card">
                          <div className="card-body">
                            <h3 className="h5 fw-bold mb-3">ملحوظات مهمة</h3>
                            <div className="alert alert-warning mb-3">
                              خدمة تجريبية وغير ملزمة من موقع <b>فيجماوية</b>
                            </div>

                            <div className="d-grid gap-2 text-muted">
                              <div>1- التسعير تقريبي ويتغير حسب نطاق المشروع الحقيقي</div>
                              <div>2- اي طلبات خارج النطاق تتحسب بالساعة</div>
                              <div>3- يفضل تثبيت المتطلبات قبل بدء التصميم لتقليل الهدر</div>
                            </div>

                            <div className="alert alert-warning mt-3 mb-0">
                              خدمة تجريبية وغير ملزمة من موقع <b>فيجماوية</b>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* PDF content (offscreen stage) */}
                <div className="pdf-stage" aria-hidden="true">
                  <div ref={reportRef} dir="rtl" className="p-3">
                    <div className="mb-3">
                      <div className="fw-bold">تقرير تسعير مشروع تصميم</div>
                      <div className="text-muted">موقع <b>فيجماوية</b></div>
                    </div>

                    <div className="alert alert-warning">
                      خدمة تجريبية وغير ملزمة من موقع <b>فيجماوية</b>
                    </div>

                    <div className="mb-3">
                      <div>اسم المشروع: {state.projectName || "-"}</div>
                      <div suppressHydrationWarning>التاريخ: {renderedAt || "-"}</div>
                      <div>تكلفة الساعة: {pricing ? formatUSD(pricing.rate) : "-"}</div>
                    </div>

                    <div className="mb-3">
                      <div className="fw-bold mb-2">النتيجة</div>
                      <div>المقترح: {pricing ? formatUSD(pricing.recommended) : "-"}</div>
                      <div>الادنى: {pricing ? formatUSD(pricing.minimum) : "-"}</div>
                      <div>الأعلى: {pricing ? formatUSD(pricing.premium) : "-"}</div>
                    </div>

                    <div className="mb-3">
                      <div className="fw-bold mb-2">التفاصيل</div>
                      {pricing ? (
                        <>
                          <div>ساعات الاساس: {pricing.baseHours}</div>
                          <div>ساعات اضافية: {pricing.addOnHours}</div>
                          <div>اجمالي الساعات: {pricing.totalHours}</div>
                          <div>قبل الاستعجال والمخاطر: {formatUSD(pricing.subtotal)}</div>
                          <div>الاستعجال: {pricing.rushMultiplier}x</div>
                          <div>نسبة المخاطر: {Math.round(pricing.riskPct * 100)}%</div>
                        </>
                      ) : null}
                    </div>

                    <div className="alert alert-warning">
                      خدمة تجريبية وغير ملزمة من موقع <b>فيجماوية</b>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="d-none d-md-block col-12 col-lg-3">
              <Stepper steps={steps} active={stepIndex} onGo={setStepIndex} />
            </div>

            <div className="col-12 col-lg-6">
              <div className="card mb-3">
                <div className="card-body">
                  <div className="text-muted small mb-1">{`خطوة ${stepIndex + 1} من ${steps.length}`}</div>
                  <h2 className="h5 fw-bold mb-3">{current.title}</h2>

                  <Fields fields={current.fields} state={state} setState={setState} />

                  <div className="d-flex justify-content-between gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                      disabled={stepIndex === 0}
                    >
                      السابق
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setStepIndex((i) => Math.min(steps.length - 1, i + 1))}
                      disabled={!canNextStep(current, state)}
                    >
                      التالي
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <h2 className="h5 fw-bold mb-3">النتيجة الحالية</h2>

                  {!pricing ? (
                    <div className="alert alert-secondary mb-0">
                      متابعة الاسئلة الاساسية عشان يظهر التسعير
                    </div>
                  ) : (
                    <>
                      <div className="d-grid gap-2 mb-3">
                        <div className="border rounded p-3">
                          <div className="text-muted small">المقترح</div>
                          <div className="fs-4 fw-bold">{formatUSD(pricing.recommended)}</div>
                        </div>

                        <div className="border rounded p-3">
                          <div className="text-muted small">الادنى</div>
                          <div className="fs-5 fw-bold">{formatUSD(pricing.minimum)}</div>
                        </div>

                        <div className="border rounded p-3">
                          <div className="text-muted small">الأعلى</div>
                          <div className="fs-5 fw-bold">{formatUSD(pricing.premium)}</div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 mb-3"
                        onClick={() =>
                          downloadPDF({
                            el: reportRef.current,
                            projectName: state.projectName,
                          })
                        }
                        disabled={!pricing}
                      >
                        تحميل PDF
                      </button>

                      <Breakdown pricing={pricing} state={state} />
                    </>
                  )}
                </div>
              </div>

              {/* PDF content (offscreen stage) */}
              <div className="pdf-stage" aria-hidden="true">
                <div ref={reportRef} dir="rtl" className="p-3">
                  <div className="mb-3">
                    <div className="fw-bold">تقرير تسعير مشروع تصميم</div>
                    <div className="text-muted">موقع <b>فيجماوية</b></div>
                  </div>

                  <div className="alert alert-warning">
                    خدمة تجريبية وغير ملزمة من موقع <b>فيجماوية</b>
                  </div>

                  <div className="mb-3">
                    <div>اسم المشروع: {state.projectName || "-"}</div>
                    <div suppressHydrationWarning>التاريخ: {renderedAt || "-"}</div>
                    <div>تكلفة الساعة: {pricing ? formatUSD(pricing.rate) : "-"}</div>
                  </div>

                  <div className="mb-3">
                    <div className="fw-bold mb-2">النتيجة</div>
                    <div>المقترح: {pricing ? formatUSD(pricing.recommended) : "-"}</div>
                    <div>الادنى: {pricing ? formatUSD(pricing.minimum) : "-"}</div>
                    <div>الأعلى: {pricing ? formatUSD(pricing.premium) : "-"}</div>
                  </div>

                  <div className="mb-3">
                    <div className="fw-bold mb-2">التفاصيل</div>
                    {pricing ? (
                      <>
                        <div>ساعات الاساس: {pricing.baseHours}</div>
                        <div>ساعات اضافية: {pricing.addOnHours}</div>
                        <div>اجمالي الساعات: {pricing.totalHours}</div>
                        <div>قبل الاستعجال والمخاطر: {formatUSD(pricing.subtotal)}</div>
                        <div>الاستعجال: {pricing.rushMultiplier}x</div>
                        <div>نسبة المخاطر: {Math.round(pricing.riskPct * 100)}%</div>
                      </>
                    ) : null}
                  </div>

                  <div className="alert alert-warning">
                    خدمة تجريبية وغير ملزمة من موقع <b>فيجماوية</b>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}

/* ----------------------------- Steps Config ----------------------------- */

function buildSteps(state) {
  return [

    {
      id: "welcome",
      title: "عن الخدمة",
      fields: [
        info("اهلا بيك في حاسبة تسعير مشاريع التصميم", [
          "الهدف اننا نساعد المبتدئين واصحاب الخبرة الادارية الضعيفة يطلعوا بتسعير منطقي وما ينسوش بنود مهمة",
          "ده اجتهادات فردية لتوضيح المجهود المبذول في المشروع",
          "القيمة الناتجة تقريبية ومش ملزمة لاي طرف",
          "الخدمة في نسختها التجريبية وسيتم تطويرها بدعمكم واقتراحاتكم",
        ]),
      ],
    },
    {
      id: "gate",
      title: "نوع المستخدم",
      fields: [
        text("projectName", "اسم المشروع", {
          help: "اسم داخلي للحفظ والتقرير",
          placeholder: "مثال: تطبيق حجوزات عيادة",
        }),
        radio(
          "userType",
          "اختار نوع المستخدم",
          [
            { value: "freelancer", label: 'فريلانسر "مستقل"', help: "حاسبة خاصة بالعمل الحر" },
            { value: "employee", label: "مصمم في شركة", help: "قريبا بنوفر نسخة للموظفين", disabled: true },
            { value: "company", label: "شركة تقنية", help: "قريبا بنوفر نسخة للشركات", disabled: true },
          ]
        ),
        // info("ملاحظة", ["اختيار واحد بس", "لو اخترت غير مستقل هتطلع رسالة اعتذار"]),
      ],
    },

    {
      id: "basics",
      title: "الاساسيات",
      fields: [
        radio("experience", "مستوى الخبرة", [
          { value: "junior", label: "Junior", help: "خبرة اقل من سنتين" },
          { value: "mid", label: "Mid", help: "خبرة 2 الى 5 سنين" },
          { value: "senior", label: "Senior", help: "خبرة 5 سنين واكثر" },
        ]),
        // text("country", "الدولة"),
        // text("city", "المدينة"),
        number("hourlyRate", "متوسط سعر الساعة بالدولار", {
          min: 2,
          help: "اقل سعر مسموح 2 دولار",
        }),
        presetHourlyRate(state),
      ],
    },

    {
      id: "size",
      title: "حجم المشروع",
      fields: [
        radio("projectSize", "اختر حجم المشروع", [
          { value: "small", label: "Small", help: "عدد شاشات قليل وتدفقات محدودة" },
          { value: "medium", label: "Medium", help: "عدد شاشات متوسط وتدفقات متعددة" },
          { value: "large", label: "Large", help: "عدد شاشات كبير وحالات كثيرة" },
        ]),
        radio("productType", "نوع المنتج", [
          { value: "website", label: "Website / Landing", help: "صفحات تعريفية او تسويقية" },
          { value: "webapp", label: "Web App", help: "تطبيق ويب فيه شغل وظائف" },
          { value: "mobile", label: "Mobile App", help: "تطبيق موبايل iOS و Android" },
          { value: "dashboard", label: "Dashboard / Admin", help: "جداول وتقارير وادارة بيانات" },
        ]),
        radio("complexity", "مستوى التعقيد العام", [
          {
            value: "basic",
            label: "Basic",
            help: "مشروع بسيط تدفقات قليلة حالات قليلة مكونات تقليدية",
          },
          {
            value: "standard",
            label: "Standard",
            help: "اغلب المشاريع تدفقات متعددة حالات Loading و Error بحث او فلترة بسيطة",
          },
          {
            value: "complex",
            label: "Complex",
            help: "صلاحيات Roles نماذج طويلة حالات كثيرة جداول و تقارير و Edge cases",
          },
        ]),
      ],
    },

    {
      id: "method",
      title: "طريقة التسعير",
      fields: [
        radio("pricingMode", "هتحسب ازاي", [
          { value: "screen", label: "بناءً على عدد الشاشات Screen-based", help: "مناسب للويبسايت والموبايل" },
          {
            value: "component",
            label: "بناءً على المكونات Component-based",
            help: "مناسب للداش بورد والديزاين سيستم",
          },
          { value: "fixed", label: "بالاتفاق Fixed project", help: "لو عندك تقدير ساعات او نطاق واضح" },
        ]),

        number("unitsCount", "عدد الشاشات او المكونات", {
          min: 1,
          visibleWhen: (s) => s.pricingMode === "screen" || s.pricingMode === "component",
        }),

        radio(
          "unitComplexity",
          "متوسط تعقيد الشاشة او المكون",
          [
            { value: "s", label: "S", help: "محتوى بسيط عناصر قليلة" },
            { value: "m", label: "M", help: "نموذج متوسط او قائمة مع فلترة بسيطة" },
            { value: "l", label: "L", help: "نموذج طويل حالات كثيرة تفاعلات معقدة" },
          ],
          {
            visibleWhen: (s) => s.pricingMode === "screen" || s.pricingMode === "component",
          }
        ),

        number("fixedHours", "تقدير ساعات اجمالي", {
          min: 1,
          help: "لو سيبتها فاضية هنستخدم تقدير تلقائي حسب حجم المشروع",
          visibleWhen: (s) => s.pricingMode === "fixed",
        }),
      ],
    },

    {
      id: "requirements",
      title: "المتطلبات والتعامل",
      fields: [
        radio("requirementsState", "ملف المتطلبات", [
          { value: "req_ready", label: "موجود وجاهز", help: "هتشتغل مباشرة على المتطلبات" },
          {
            value: "req_write",
            label: "مش موجود والعميل عايزك تكتبه",
            help: "وقت اضافي لصياغة المتطلبات والتوثيق",
          },
          {
            value: "req_discovery",
            label: "مش موجود وهنستخرجه بجلسات تعريف",
            help: "جلسات اسئلة وتحديد نطاق ومخرجات واضحة",
          },
        ]),
        radio("clientLevel", "ثقافة العميل عن المجال", [
          { value: "client_tech", label: "تقني او مصمم", help: "شرح اقل وقرارات اسرع" },
          { value: "client_mid", label: "متوسط", help: "هتحتاج توضيح امثلة وبعض البدائل" },
          {
            value: "client_nontech",
            label: "غير تقني",
            help: "وقت اكبر للشرح ونقل المعرفة عشان القرار يبقى مبني على رؤية تقنية صحيحة",
          },
        ]),
        info("ليه ده مهم", [
          "العميل غير التقني غالبا محتاج ترجمة القيود والامكانيات",
          "ده يوفر له قرارات ادق ويقلل تراجع القرارات لاحقا",
        ]),
        radio("meetingsRate", "معدل الاجتماعات الاسبوعية", [
          { value: "meet_low", label: "قليل 0-1", help: "متابعة بسيطة" },
          { value: "meet_mid", label: "متوسط 2-3", help: "متابعة طبيعية" },
          { value: "meet_high", label: "عالي 4+", help: "تنسيق كثير وتحديثات مستمرة" },
        ]),
      ],
    },

    {
      id: "ux",
      title: "تصميم فقط ؟",
      fields: [
        radio("scopeType", "نطاق الشغل", [
          { value: "scope_ui", label: "UI فقط", help: "تطبيق بصري بناء على متطلبات جاهزة" },
          { value: "scope_uxui_lite", label: "UX + UI Lite", help: "Flows + Wireframes بشكل عملي سريع" },
          {
            value: "scope_uxui_standard",
            label: "UX + UI Standard",
            help: "العمل على المشروع بمنهجية التفكير التصميمي بكل تفاصيلها",
          },
        ]),
        info("ملاحظة", [
          "الـ UX محتاج وقت اضافي للبحث والتحقق",
          "خبرات الفرونت اند بتساعد تقلل الهدر في قرارات مش قابلة للتنفيذ",
        ]),
      ],
    },

    {
      id: "brand",
      title: "ملفات الهوية Brand files",
      fields: [
        radio("brandReadiness", "جاهزية البراند", [
          { value: "brand_guidelines", label: "Brand guidelines جاهزة", help: "وقت اقل لضبط الهوية" },
          { value: "brand_logo", label: "Logo فقط", help: "وقت اضافي لتحديد الالوان والخطوط" },
          { value: "brand_basickit", label: "لا يوجد Basic kit", help: "وقت اكبر لبناء حزمة اساسية قبل UI" },
        ]),
      ],
    },

    {
      id: "delivery",
      title: "التسليم والمراجعات",
      fields: [
        radio("deliveryType", "تسليم الملفات", [
          { value: "delivery_prototype", label: "Prototype فقط", help: "مناسب للعرض والاتفاق" },
          { value: "delivery_figma", label: "Figma file منظم", help: "ملفات مرتبة ومسميات وملاحظات" },
          {
            value: "delivery_library",
            label: "Library Components + Tokens",
            help: "وقت اضافي لبناء مكتبة قابلة لاعادة الاستخدام",
          },
        ]),
        radio("handoffLevel", "التسليم للمطورين Handoff", [
          { value: "handoff_basic", label: "Basic specs", help: "مقاسات ومسافات اساسية" },
          { value: "handoff_detailed", label: "Detailed specs", help: "حالات ومكونات وتفاصيل تفاعلات" },
        ]),
        radio("includedRevisions", "جولات المراجعة المشمولة", [
          { value: "rev_1", label: "1", help: "ملاحظات مجمعة مرة واحدة" },
          { value: "rev_2", label: "2", help: "مرتين ملاحظات مجمعة" },
          { value: "rev_3", label: "3", help: "ثلاث جولات" },
        ]),
        info("تعريف الجولة", ["الجولة معناها ملاحظات مجمعة مرة واحدة", "مش تعديل يومي على الشات"]),
      ],
    },

    {
      id: "risk",
      title: "الوقت والمخاطر",
      fields: [
        radio("timelineType", "مستوى الاستعجال", [
          { value: "normal", label: "مشروع عادي", help: "مواعيد طبيعية بدون ضغط" },
          { value: "rush", label: "مشروع عاجل", help: "ضغط وقت وتبديل اولويات" },
          { value: "rush_very", label: "مشروع عاجل جدا", help: "ضغط عالي جدا وتفرغ اكبر" },
        ]),
        radio("riskBuffer", "Buffer مخاطر", [
          { value: "r5", label: "5%", help: "مشروع واضح ونطاق ثابت" },
          { value: "r10", label: "10%", help: "اغلب المشاريع" },
          { value: "r20", label: "20%", help: "نطاق متغير او عميل غير واضح" },
        ]),
      ],
    },
    {
      id: "final",
      title: "النتيجة النهائية",
      fields: [
        info("ملخص نهائي", [
          "هنا بنعرض التسعير النهائي بشكل اكبر مع ملخص الافتراضات",
          "تقدر ترجع لاي خطوة وتعدل البيانات وهنحدث النتيجة تلقائيا",
        ]),
      ],
    }

  ];
}

function defaultState() {
  return {
    projectName: "",
    userType: "",
    experience: "junior",
    // country: "",
    // city: "",
    hourlyRate: "",

    projectSize: "small",
    productType: "webapp",
    complexity: "standard",

    pricingMode: "screen",
    unitsCount: "",
    unitComplexity: "m",
    fixedHours: "",

    requirementsState: "req_ready",
    clientLevel: "client_mid",
    meetingsRate: "meet_mid",

    scopeType: "scope_ui",
    brandReadiness: "brand_guidelines",

    deliveryType: "delivery_figma",
    handoffLevel: "handoff_basic",
    includedRevisions: "rev_2",

    timelineType: "normal",
    riskBuffer: "r10",
  };
}

function isReadyForPricing(s) {
  const rate = Number(s.hourlyRate || 0);
  if (!s.userType) return false;
  if (s.userType !== "freelancer") return false;
  if (String(s.projectName || "").trim().length < 2) return false;
  // if (!s.country || !s.city) return false;
  if (!s.experience) return false;
  if (!(rate >= 2)) return false;

  if (!s.projectSize || !s.productType || !s.complexity) return false;
  if (!s.pricingMode) return false;

  if (s.pricingMode === "screen" || s.pricingMode === "component") {
    if (!(Number(s.unitsCount || 0) > 0)) return false;
    if (!s.unitComplexity) return false;
  }

  return true;
}

function canNextStep(step, s) {
  if (step.id === "welcome") return true;
  if (step.id === "gate") {
    if (!s.userType) return false;
    if (s.userType === "freelancer") return String(s.projectName || "").trim().length >= 2;
    return true;
  }

  if (step.id === "basics") {
    return (
      Boolean(s.experience) &&
      // Boolean(s.country) &&
      // Boolean(s.city) &&
      Number(s.hourlyRate || 0) >= 2
    );
  }

  if (step.id === "method") {
    if (!s.pricingMode) return false;
    if (s.pricingMode === "screen" || s.pricingMode === "component") {
      return Number(s.unitsCount || 0) > 0 && Boolean(s.unitComplexity);
    }
    return true;
  }

  if (step.id === "risk") {
    return isReadyForPricing(s);
  }

  return true;
}

/* ----------------------------- Field Builders ----------------------------- */

function radio(name, label, options, extra = {}) {
  return { type: "radio", name, label, options, ...extra };
}
function text(name, label, extra = {}) {
  return { type: "text", name, label, ...extra };
}
function number(name, label, extra = {}) {
  return { type: "number", name, label, ...extra };
}
function info(title, lines) {
  return { type: "info", title, lines };
}

function presetHourlyRate(state) {
  const exp = state.experience || "junior";
  const r = PRESETS.hourlyRanges[exp] || PRESETS.hourlyRanges.junior;

  return {
    // type: "presetRate",
    // name: "hourlyRate",
    // label: "نطاق مقترح حسب الخبرة",
    // data: r,
  };
}

/* ----------------------------- UI Components ----------------------------- */

function Shell({ children }) {
  return (
    <div dir="rtl" className="container py-4">
      {children}
    </div>
  );
}

function Stepper({ steps, active, onGo }) {
  return (
    <div className="list-group">
      {steps.map((s, idx) => (
        <button
          key={s.id}
          type="button"
          className={`list-group-item list-group-item-action ${idx === active ? "active" : ""}`}
          onClick={() => onGo(idx)}
        >
          {idx + 1}- {s.title}
        </button>
      ))}
    </div>
  );
}

function Fields({ fields, state, setState }) {
  return (
    <div className="d-grid gap-3">
      {fields.map((f, i) => {
        if (typeof f.visibleWhen === "function" && !f.visibleWhen(state)) return null;

        if (f.type === "info") {
          return (
            <div className="alert alert-secondary mb-0" key={i}>
              <div className="fw-bold mb-2">{f.title}</div>
              <div className="d-grid gap-1">
                {f.lines.map((x, idx) => (
                  <div key={idx}>
                    {idx + 1}- {x}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (f.type === "text") {
          return (
            <Field key={i} label={f.label} help={f.help}>
              <input
                className="form-control"
                value={state[f.name] || ""}
                onChange={(e) => setState((s) => ({ ...s, [f.name]: e.target.value }))}
                placeholder={f.placeholder || ""}
              />
            </Field>
          );
        }

        if (f.type === "number") {
          const val = state[f.name] ?? "";
          const showErr =
            f.name === "hourlyRate" && Number(val || 0) > 0 && Number(val || 0) < 2;

          return (
            <Field key={i} label={f.label} help={f.help}>
              <input
                className="form-control"
                type="number"
                min={f.min}
                value={val}
                onChange={(e) => setState((s) => ({ ...s, [f.name]: e.target.value }))}
              />
              {showErr ? (
                <div className="text-danger small mt-2">اقل سعر مسموح 2 دولار</div>
              ) : null}
            </Field>
          );
        }

        if (f.type === "radio") {
          const groupName = `fgw_${f.name}`;

          return (
            <Field key={i} label={f.label} help={f.help}>
              <div className="d-grid gap-2">
                {f.options.map((opt, idx) => {
                  const selected = state[f.name] === opt.value;
                  const id = `${groupName}_${idx}`;
                  const hint = opt.calc ?? getCalcHint(f.name, opt.value, state);

                  return (
                    <div
                      key={opt.value}
                      className={`border rounded p-2 ${selected ? "border-primary" : ""} ${opt.disabled ? "opacity-50" : ""
                        }`}
                      style={{ cursor: opt.disabled ? "not-allowed" : "pointer" }}
                      onClick={() => !opt.disabled && setState((s) => ({ ...s, [f.name]: opt.value }))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          !opt.disabled && setState((s) => ({ ...s, [f.name]: opt.value }));
                        }
                      }}
                    >
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <div className="form-check m-0">
                          <input
                            className="form-check-input"
                            type="radio"
                            name={groupName}
                            id={id}
                            checked={selected}
                            disabled={Boolean(opt.disabled)}
                            onChange={() => setState((s) => ({ ...s, [f.name]: opt.value }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <label
                            className="form-check-label fw-semibold w-100"
                            htmlFor={id}
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: opt.disabled ? "not-allowed" : "pointer" }}
                          >
                            {opt.label}
                          </label>
                        </div>

                        {hint ? (
                          <span className={`badge ${selected ? "text-bg-primary" : "text-bg-light"}`}>
                            {hint}
                          </span>
                        ) : null}
                      </div>

                      {opt.help ? <div className="text-muted small mt-1">{opt.help}</div> : null}
                    </div>
                  );
                })}
              </div>
            </Field>
          );
        }

        if (f.type === "presetRate") {
          const { min, max, suggested } = f.data;
          return (
            <div className="card" key={i}>
              <div className="card-body">
                <div className="fw-bold mb-2">{f.label}</div>
                <div className="d-grid gap-1 text-muted">
                  <div>1- النطاق المقترح {min} الى {max} دولار</div>
                  <div>2- اقتراح سريع {suggested} دولار</div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-primary mt-3"
                  onClick={() => setState((s) => ({ ...s, hourlyRate: String(suggested) }))}
                >
                  استخدم الاقتراح
                </button>
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

function Field({ label, help, children }) {
  return (
    <div>
      <div className="fw-bold mb-1">{label}</div>
      {help ? <div className="text-muted small mb-2">{help}</div> : null}
      {children}
    </div>
  );
}

function Breakdown({ pricing, state }) {
  return (
    <div className="d-grid gap-2">
      <Line k="اسم المشروع" v={state.projectName || "-"} />
      <Line k="سعر الساعة" v={formatUSD(pricing.rate)} />
      <Line k="ساعات الاساس" v={pricing.baseHours} />
      <Line k="ساعات اضافية" v={pricing.addOnHours} />
      <Line k="اجمالي الساعات" v={pricing.totalHours} />
      <Line k="قبل الاستعجال والمخاطر" v={formatUSD(pricing.subtotal)} />
      <Line k="الاستعجال" v={`${pricing.rushMultiplier}x`} />
      <Line k="Buffer مخاطر" v={`${Math.round(pricing.riskPct * 100)}%`} />

      <div className="alert alert-secondary mb-0 mt-2">
        <div className="fw-bold mb-2">افتراضات سريعة</div>
        <div className="d-grid gap-1">
          <div>1- جولات المراجعة المشمولة {state.includedRevisions?.replace("rev_", "")}</div>
          <div>2- اي طلبات خارج النطاق تتحسب بالساعة</div>
          <div>3- الجولة معناها ملاحظات مجمعة مرة واحدة</div>
        </div>
      </div>
    </div>
  );
}

function Line({ k, v }) {
  return (
    <div className="d-flex justify-content-between gap-2">
      <div className="text-muted">{k}</div>
      <div className="fw-semibold">{v}</div>
    </div>
  );
}


function getCalcHint(fieldName, value, state) {
  try {
    // Multipliers (overall base hours)
    if (fieldName === "productType") {
      const m = PRESETS.productMultiplier?.[value];
      if (Number.isFinite(m)) return `x${m}`;
    }

    if (fieldName === "complexity") {
      const m = PRESETS.complexityMultiplier?.[value];
      if (Number.isFinite(m)) return `x${m}`;
    }

    // Base hours by size (fallback estimate)
    if (fieldName === "projectSize") {
      const h = PRESETS.sizeBaseHours?.[value];
      if (Number.isFinite(h)) return `اساس ${h} ساعة`;
    }

    // Unit hours (screen/component complexity)
    if (fieldName === "unitComplexity") {
      const h = PRESETS.unitHours?.[value];
      if (Number.isFinite(h)) return `${h} ساعة لكل وحدة`;
    }

    // Add-on hours (requirements, client level, meetings, scope, brand, delivery, handoff, revisions)
    const add = PRESETS.addOnsHours?.[value];
    if (Number.isFinite(add)) {
      if (add === 0) return `+0 ساعة`;
      return `+${add} ساعة`;
    }

    // Rush
    if (fieldName === "timelineType") {
      const m = value === "rush" ? 1.25 : (value === "rush_very" ? 1.5 : 1.0);
      return `x${m}`;
    }

    // Risk buffer
    if (fieldName === "riskBuffer") {
      const pct = PRESETS.riskBufferPct?.[value];
      if (Number.isFinite(pct)) return `+${Math.round(pct * 100)}%`;
    }

    // Pricing mode (display method)
    if (fieldName === "pricingMode") {
      if (value === "screen") return "حسب عدد الشاشات";
      if (value === "component") return "حسب عدد المكونات";
      if (value === "fixed") return "ساعات تقديرية";
    }

    return "";
  } catch {
    return "";
  }
}

function formatUSD(n) {
  const x = Number(n || 0);
  return `$${x.toFixed(2)}`;
}
